/* Proposal workspace shared with desktop. It loads the actual role-selected
   DOCX, keeps generation/history/legacy pricing, and uses one in-document
   approval action. Root edits; selected reviewers see the complete client file. */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { proposalApi, proposalAccess, ProposalMeta, ProposalFull, ProposalRun, ProposalSpec, ProposalPricing } from '../../architect/proposalApi';
import { architectApi, WorkflowRecoveryResult } from '../../architect/api';
import { useAuth } from '../../auth/AuthContext';
import { fmtDate, modelDisplayEstimate } from '../../architect/utils';
import { useMainButton, haptic } from '../telegram';
import { RunCard } from '../components/RunCard';
import { Sheet } from '../components/Sheet';
import { ApprovalMeter, useApprovalStatus, useApprovalStatuses } from '../components/DocSheet';
import '../tma-docs.css';
import type { CommentAnchor } from '../../architect/api';
import { CommentPinRef } from '../../architect/CommentAnchors';
import { useWorkspaceDraftGuard, useWorkspaceLeaveGuard } from '../../architect/useWorkspaceDraftGuard';
import ProposalClientEditor from '../../architect/ProposalClientEditor';
import { StageHeader, StageFooter, StageAiButton } from '../../architect/StageChrome';
import { deliverFile } from '../files';
import { Download } from 'lucide-react';
import WorkflowRecoveryDialog from '../../architect/WorkflowRecoveryDialog';

export interface ProposalTabProps {
  projectId: string;
  /** Historical document behind a locked pipeline stage; viewing only. */
  readOnly?: boolean;
  /** Whether the project's architecture is approved — one of the two upstream
      gates for generation (the other, an approved estimate, is only known to
      the server). Drives the generate hint. */
  architectureApproved: boolean;
  estimateApproved?: boolean;
  pricingApproved?: boolean;
  proposalRunning?: boolean;
  onPipelineChange: () => void;
  onWorkflowRecovered?: (result: WorkflowRecoveryResult) => void | Promise<void>;
  onCommentAnchor?: (anchor: CommentAnchor) => void;
  commentPins?: CommentPinRef[];
  onOpenCommentThread?: (id: string) => void;
  pickingComment?: boolean;
  jumpAnchor?: CommentAnchor | null;
  commentsOpen?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  review: 'In review',
  approved: 'Approved',
};

export function ProposalTab({
  projectId,
  readOnly = false,
  pricingApproved = false,
  proposalRunning = false,
  onPipelineChange,
  onWorkflowRecovered,
  jumpAnchor,
  onCommentAnchor,
  pickingComment,
}: ProposalTabProps) {
  const { user, permissions } = useAuth();
  const { isRoot, canGenerate, canSee } = useMemo(
    () => proposalAccess(user, permissions),
    [user, permissions],
  );

  const [proposals, setProposals] = useState<ProposalMeta[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [recoveryProposalId, setRecoveryProposalId] = useState<string | null>(null);
  const [listProject, setListProject] = useState<string | null>(null);

  const [pendingRun, setPendingRun] = useState<ProposalRun | null>(null);
  const [runBusy, setRunBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [resumeState, setResumeState] = useState<{ projectId: string; idle: boolean } | null>(null);
  const resumeIdle = resumeState?.projectId === projectId && resumeState.idle;
  const onResumeResult = useCallback((run: ProposalRun | null) => setResumeState({ projectId, idle: run == null || run.status === 'error' }), [projectId]);
  const [runError, setRunError] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);

  // Native document viewer (full-screen overlay).
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [initialAskOpen, setInitialAskOpen] = useState(false);
  const [viewer, setViewer] = useState<ProposalFull | null>(null);
  const [legacyPricing, setLegacyPricing] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingDraft, setPricingDraft] = useState<ProposalPricing | null>(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);

  // A legacy proposal without a project price sheet still uses its original
  // editor. New projects must edit and approve prices in the Pricing stage.
  useEffect(() => {
    let live = true;
    setLegacyPricing(false);
    if (isRoot && !readOnly) {
      void architectApi.getPricing(projectId).then(result => {
        if (live) setLegacyPricing(result.legacy_pricing === true && result.has_project_sheet === false);
      }).catch(() => { if (live) setLegacyPricing(false); });
    }
    return () => { live = false; };
  }, [isRoot, readOnly, projectId]);

  useWorkspaceDraftGuard(() => ({
    dirty: !!pricingOpen && !!pricingDraft && !!viewer && JSON.stringify(pricingDraft) !== JSON.stringify(normalisePricing(viewer.pricing, viewer.spec)),
    busy: savingPricing,
  }));
  const requestLeave = useWorkspaceLeaveGuard();

  // Bumped after a vote so the counters refetch even when the row set has not
  // moved (a recorded vote below the threshold changes nothing else).
  const [apprRev, setApprRev] = useState(0);

  const load = useCallback(
    async (quiet = false) => {
      try {
        const r = await proposalApi.listProposals(projectId);
        setProposals(r.proposals || []);
        setListProject(projectId);
        if (!quiet) setLoadError(null);
      } catch (err: unknown) {
        if (!quiet) setLoadError((err as Error).message || 'Could not load proposals');
        setProposals((prev) => prev || []);
        setListProject(null);
      }
    },
    [projectId],
  );

  useEffect(() => {
    if (canSee) void load();
  }, [canSee, load]);

  /* ── approvals (DESIGN23 stage 1) ────────────────────────
     One status for the open document (where Approve lives) plus a compact
     counter on the in-review rows. A failed read means «no policy», i.e.
     exactly the pre-stage-1 behaviour — never a hidden button. */
  const approvalIds = useMemo(
    () => (proposals || []).map((p) => p.id),
    [proposals],
  );
  const rowApprovals = useApprovalStatuses(
    'proposal',
    'proposal',
    approvalIds,
    canSee,
    apprRev,
  );
  const apprStatus = useApprovalStatus(
    'proposal',
    'proposal',
    viewer ? viewer.id : null,
    canSee,
    apprRev,
  );

  const onRunDone = useCallback(
    (run: ProposalRun) => {
      setPendingRun(null);
      setRunError(null);
      void load(true);
      if (run.proposal_id) {
        const id = run.proposal_id;
        setFlashId(id);
        window.setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 1600);
      }
      onPipelineChange();
    },
    [load, onPipelineChange],
  );

  const onRunError = useCallback((message: string) => {
    setPendingRun(null);
    setRunError(message);
  }, []);

  const startProposal = useCallback(async () => {
    if (listProject !== projectId || !canGenerate || readOnly || !pricingApproved || !resumeIdle || proposalRunning || runBusy || starting || pendingRun || loadError || proposals?.length !== 0) return;
    setStarting(true); setRunError(null);
    try {
      const { run } = await proposalApi.generateProposal(projectId);
      setPendingRun(run);
      onPipelineChange();
    } catch (error) { setRunError((error as Error).message || 'Could not start the proposal.'); }
    finally { setStarting(false); }
  }, [listProject, canGenerate, readOnly, pricingApproved, resumeIdle, proposalRunning, runBusy, starting, pendingRun, loadError, proposals, projectId, onPipelineChange]);

  /* ── native document viewer ─────────────────────────────── */
  const openViewer = useCallback(async (prop: ProposalMeta, ask = false) => {
    haptic.impact('light');
    setViewerId(prop.id);
    setInitialAskOpen(ask);
    setViewer(null);
    setPricingOpen(false);
    setPricingDraft(null);
    try {
      const { proposal } = await proposalApi.getProposal(prop.id);
      setViewerId((cur) => {
        if (cur === prop.id) setViewer(proposal);
        return cur;
      });
    } catch (err: unknown) {
    }
  }, []);
  const autoOpened = React.useRef(false);
  useEffect(() => {
    if (readOnly || autoOpened.current || !proposals?.length) return;
    autoOpened.current = true;
    void openViewer(proposals[0]);
  }, [readOnly, proposals, openViewer]);
  useEffect(() => {
    if (!jumpAnchor || !proposals?.length) return;
    const target = proposals.find(item => item.id === jumpAnchor.document_id);
    if (target && viewerId !== target.id) void openViewer(target);
  }, [jumpAnchor, proposals, viewerId, openViewer]);
  const closeViewer = useCallback(() => {
    haptic.impact('light');
    setViewerId(null);
    setInitialAskOpen(false);
    setViewer(null);
    setPricingOpen(false);
    setPricingDraft(null);
  }, []);

  // History remains downloadable even if the embedded document viewer fails.
  // Reuse the server-authorized Telegram delivery path, never a public blob URL.
  const downloadSaved = async (id: string) => {
    if (downloadingId) return;
    setDownloadingId(id);
    setDownloadError(null);
    try { await deliverFile('proposal_docx', id); }
    catch (error) { setDownloadError((error as Error).message || 'Could not download this proposal.'); }
    finally { setDownloadingId(null); }
  };

  /* ── pricing editor (ROOT ONLY) ─────────────────────────── */
  const openPricing = useCallback(() => {
    if (!viewer || viewer.status === 'approved' || !isRoot || !legacyPricing || readOnly) return;
    haptic.impact('light');
    setPricingDraft(normalisePricing(viewer.pricing, viewer.spec));
    setPricingError(null);
    setPricingOpen(true);
  }, [viewer, isRoot, legacyPricing, readOnly]);

  const savePricing = useCallback(async () => {
    if (!viewer || viewer.status === 'approved' || !pricingDraft || savingPricing || !legacyPricing || readOnly) return;
    setSavingPricing(true);
    setPricingError(null);
    try {
      const { proposal } = await proposalApi.updatePricing(viewer.id, pricingDraft);
      setViewer(proposal);
      setPricingDraft(normalisePricing(proposal.pricing, proposal.spec));
      haptic.notify('success');
      setPricingOpen(false);
      await load(true);
      setApprRev(value => value + 1);
      onPipelineChange();
    } catch (err: unknown) {
      haptic.notify('error');
      setPricingError((err as Error).message || 'Could not save prices');
    } finally {
      setSavingPricing(false);
    }
  }, [viewer, pricingDraft, savingPricing, legacyPricing, readOnly, load, onPipelineChange]);

  const documentReadOnly = readOnly || viewer?.status === 'approved';
  // The shared document footer owns approval; never mount a second native action.
  useMainButton({ text: 'Approve', visible: false, onClick: () => {} });

  /* ── defensive: not a generator ─────────────────────────── */
  if (!canSee) {
    return (
      <div className="tma-doc-tab">
        <div className="tma-doc-notice">
          <div className="tma-doc-notice-emoji" aria-hidden="true">
            🔒
          </div>
          <div className="tma-doc-notice-title">Proposal isn't available</div>
          <div className="tma-doc-notice-sub">
            You do not have access to this stage.
          </div>
        </div>
      </div>
    );
  }

  const list = proposals || [];

  return (
    <div className={'tma-doc-tab tma-native-proposal stage-mini-surface' + (viewerId ? ' has-viewer' : '')}>
      {!viewerId ? <StageHeader title="Proposal" status={readOnly ? 'Saved history' : 'Version history'} actions={list[0] ? [{ id: 'latest', label: 'Open latest proposal', onClick: () => { void openViewer(list[0]); } }] : []}/> : null}
      {!readOnly ? <RunCard<ProposalRun>
        projectId={projectId}
        run={pendingRun}
        poll={proposalApi.getProposalRun}
        canCancel={canGenerate && !readOnly}
        cancel={proposalApi.cancelProposalRun}
        getActive={proposalApi.getActiveProposalRun}
        heading="Preparing the proposal"
        modelLabel={modelDisplayEstimate}
        steps={['Prepare sources', 'AI writes the proposal', 'Validate', 'Render', 'Save']}
        onDone={onRunDone}
        onError={onRunError}
        onBusyChange={setRunBusy}
        onResumeResult={onResumeResult}
      /> : null}

      {runError ? (
        <div className="tma-doc-banner err" role="alert">
          {runError}
        </div>
      ) : null}
      {loadError ? (
        <div className="tma-doc-banner err" role="alert">
          {loadError}
        </div>
      ) : null}
      {downloadError ? <div className="tma-doc-banner err" role="alert">{downloadError}</div> : null}

      {proposals === null ? (
        <div className="tma-doc-list">
          {[0, 1].map((i) => (
            <div className="tma-doc-row tma-doc-row-skel" key={i}>
              <div className="tma-doc-skel tma-doc-skel-a" />
              <div className="tma-doc-skel tma-doc-skel-b" />
            </div>
          ))}
        </div>
      ) : list.length ? (
        <div className="tma-doc-list">
          {list.map((prop) => (
            <div key={prop.id} className={readOnly ? 'tma-doc-history-entry' : undefined}>
            <button
              type="button"
              className={'tma-doc-row' + (prop.id === flashId ? ' flash' : '')}
              onClick={() => openViewer(prop)}
            >
              <div className="tma-doc-row-head">
                <span className={'tma-doc-pill ' + prop.status}>
                  {STATUS_LABEL[prop.status] || prop.status}
                </span>
                <span className="tma-doc-model">{modelDisplayEstimate(prop.model)}</span>
                <span className="tma-doc-date">{fmtDate(prop.created_at)}</span>
              </div>
              <div className="tma-doc-row-title">
                {prop.project || prop.client || 'Proposal'}
              </div>
              <div className="tma-doc-row-meta">
                {prop.created_by_name ? 'Created by ' + prop.created_by_name : 'Created'}
                {prop.status === 'approved' && prop.approved_by_name
                  ? ' · approved by ' + prop.approved_by_name
                  : ''}
              </div>
              {prop.recovery ? <div className="tma-doc-row-meta">Returned by {prop.recovery.restored_by_name} · {fmtDate(prop.recovery.restored_at)}</div> : null}
              {/* The exact client document is selected by server permissions. */}
              <span className={'tma-doc-row-priced' + (prop.priced ? '' : ' pending')}>
                {prop.priced
                  ? 'Prices set'
                  : 'Awaiting prices'}
              </span>
              <ApprovalMeter
                status={rowApprovals[prop.id]}
                pending={prop.status === 'review'}
              />
              <span className="tma-doc-row-open" aria-hidden="true">
                Open ›
              </span>
            </button>
            {readOnly ? <div className="tma-doc-history-actions">
            {prop.has_docx ? <button type="button" className="tma-doc-btn tma-doc-btn-block" disabled={!!downloadingId}
              onClick={() => void downloadSaved(prop.id)} aria-label={`Download ${prop.project || prop.client || 'proposal'}`}>
              <Download size={16} aria-hidden="true" /> {downloadingId === prop.id ? 'Preparing…' : 'Download DOCX'}
            </button> : null}
            {isRoot && onWorkflowRecovered ? <button type="button" className="tma-doc-btn tma-doc-btn-block" onClick={() => setRecoveryProposalId(prop.id)}>Return to work</button> : null}
            </div> : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="tma-doc-empty">
          <div className="tma-doc-empty-emoji" aria-hidden="true">
            📄
          </div>
          <div className="tma-doc-empty-title">No proposals yet</div>
          <div className="tma-doc-empty-sub">
            {pricingApproved ? 'Pricing is approved. The proposal will appear here when ready.' : 'Approve Pricing to prepare the proposal.'}
          </div>
          {listProject === projectId && canGenerate && !readOnly && pricingApproved && !proposalRunning && resumeIdle && !runBusy && !pendingRun && !loadError && proposals?.length === 0 ? <button type="button" className="tma-doc-btn tma-doc-btn-primary tma-doc-btn-block" disabled={starting} onClick={() => void startProposal()}>{starting ? 'Starting…' : runError ? 'Retry proposal' : 'Generate proposal'}</button> : null}
        </div>
      )}

      {!viewerId ? <>
        {canGenerate && !readOnly ? <StageAiButton mini disabled={!list[0]} onClick={() => { if (list[0]) void openViewer(list[0], true); }}/> : null}
        {!readOnly ? <StageFooter stage="proposal" className="stage-mini-footer" pending={list[0]?.status !== 'approved'} status={list[0] ? rowApprovals[list[0].id] : undefined} title={list.length ? 'Version history' : 'Proposal not started'}/> : null}
      </> : null}
      <div className="tma-doc-mb-spacer" />
      {isRoot && recoveryProposalId && onWorkflowRecovered ? <WorkflowRecoveryDialog open variant="tma" projectId={projectId} proposalId={recoveryProposalId}
        onClose={() => setRecoveryProposalId(null)} onRestored={async result => { await onWorkflowRecovered(result); setRecoveryProposalId(null); }} /> : null}

      {/* The actual DOCX file is shared with the desktop workspace. */}
      {viewerId ? <div className="tma-doc-viewer" aria-label="Proposal document">
        <ProposalClientEditor proposalId={viewerId} access={proposalAccess(user, permissions)}
          presentation="workspace" mini readOnly={readOnly} initialAskOpen={initialAskOpen} revision={apprRev}
          extraAction={current => isRoot && legacyPricing && !current.priced ? <button type="button" onClick={openPricing}>Set prices</button> : null}
          approvalStatus={apprStatus} approvalUserId={user?.id}
          onCommentAnchor={onCommentAnchor} pickingComment={pickingComment} jumpAnchor={jumpAnchor}
          onChanged={() => { void load(true); setApprRev(value => value + 1); onPipelineChange(); }}
          onClose={changed => { closeViewer(); if (changed) { void load(true); onPipelineChange(); } }} />
      </div> : null}

      {/* Pricing editor — ROOT ONLY (§T2.3) */}
      {isRoot && legacyPricing && !documentReadOnly ? (
        <Sheet
          open={pricingOpen}
          onClose={() => requestLeave(() => setPricingOpen(false))}
          title="Pricing"
          headerAction={
            <button
              type="button"
              className="tma-sheet-text-btn"
              disabled={savingPricing}
              onClick={() => requestLeave(() => setPricingOpen(false))}
            >
              Cancel
            </button>
          }
          footer={
            <button
              type="button"
              className="tma-doc-btn tma-doc-btn-primary tma-doc-btn-block"
              disabled={savingPricing || !pricingDraft}
              onClick={savePricing}
            >
              {savingPricing ? 'Saving…' : 'Save prices'}
            </button>
          }
        >
          {pricingError ? <div className="tma-doc-banner err" role="alert">{pricingError}</div> : null}
          {viewer && pricingDraft ? (
            <PricingEditor
              spec={viewer.spec}
              draft={pricingDraft}
              disabled={savingPricing}
              onChange={setPricingDraft}
            />
          ) : (
            <div className="tma-doc-field-hint">Loading…</div>
          )}
        </Sheet>
      ) : null}
    </div>
  );
}

/* ═══════════ pricing editor (root) ══════════════════════════════ */

function PricingEditor({
  spec,
  draft,
  disabled,
  onChange,
}: {
  spec: ProposalSpec;
  draft: ProposalPricing;
  disabled: boolean;
  onChange: (d: ProposalPricing) => void;
}) {
  const setupFees = spec.setup_fees || [];
  const monthlyFees = spec.monthly_fees || [];

  const setSetup = (i: number, price: string) =>
    onChange({ ...draft, setup: draft.setup.map((s) => (s.i === i ? { ...s, price } : s)) });
  const setMonthly = (i: number, price: string) =>
    onChange({ ...draft, monthly: draft.monthly.map((s) => (s.i === i ? { ...s, price } : s)) });
  const setOverage = (i: number, overage: string) =>
    onChange({ ...draft, monthly: draft.monthly.map((s) => (s.i === i ? { ...s, overage } : s)) });
  const setWeeks = (idx: number, weeks: string) =>
    onChange({
      ...draft,
      case_weeks: draft.case_weeks.map((c, j) => (j === idx ? { ...c, weeks } : c)),
    });
  const setTotalWeeks = (total_weeks: string) => onChange({ ...draft, total_weeks });

  return (
    <div className="tma-doc-pricing">
      {setupFees.length ? (
        <div className="tma-doc-pricing-group">
          <div className="tma-doc-pricing-title">One-time setup</div>
          {setupFees.map((fee, i) => {
            const row = draft.setup.find((s) => s.i === i);
            return (
              <div className="tma-doc-pricing-row col" key={i}>
                <label className="tma-doc-pricing-label" htmlFor={'tma-price-setup-' + i}>
                  {fee.case || 'Setup ' + (i + 1)}
                </label>
                <input
                  id={'tma-price-setup-' + i}
                  className="tma-doc-input"
                  type="text"
                  inputMode="text"
                  placeholder="$XX,XXX.00"
                  value={row?.price || ''}
                  disabled={disabled}
                  onChange={(e) => setSetup(i, e.target.value)}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      {monthlyFees.length ? (
        <div className="tma-doc-pricing-group">
          <div className="tma-doc-pricing-title">Monthly</div>
          {monthlyFees.map((fee, i) => {
            const row = draft.monthly.find((s) => s.i === i);
            return (
              <div className="tma-doc-pricing-row col" key={i}>
                <label className="tma-doc-pricing-label" htmlFor={'tma-price-monthly-' + i}>
                  {fee.label || 'Monthly ' + (i + 1)}
                </label>
                <input
                  id={'tma-price-monthly-' + i}
                  className="tma-doc-input"
                  type="text"
                  placeholder="$XXX.00"
                  value={row?.price || ''}
                  disabled={disabled}
                  onChange={(e) => setMonthly(i, e.target.value)}
                />
                <input
                  className="tma-doc-input"
                  type="text"
                  placeholder="Overage — e.g. +$1 per extra request (optional)"
                  value={row?.overage || ''}
                  disabled={disabled}
                  onChange={(e) => setOverage(i, e.target.value)}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      {!setupFees.length && !monthlyFees.length ? (
        <div className="tma-doc-field-hint">
          This proposal has no fee blocks yet — regenerate it, or add them on the
          web before pricing.
        </div>
      ) : null}

      <div className="tma-doc-pricing-group">
        <div className="tma-doc-pricing-title">Timeline (weeks)</div>
        {draft.case_weeks.map((cw, j) => (
          <div className="tma-doc-pricing-row" key={j}>
            <label className="tma-doc-pricing-label">{cw.case || 'Case ' + (j + 1)}</label>
            <input
              className="tma-doc-input tma-doc-input-weeks"
              type="text"
              inputMode="numeric"
              placeholder="wks"
              value={cw.weeks || ''}
              disabled={disabled}
              onChange={(e) => setWeeks(j, e.target.value)}
            />
          </div>
        ))}
        <div className="tma-doc-pricing-row">
          <label className="tma-doc-pricing-label">Total (weeks)</label>
          <input
            className="tma-doc-input tma-doc-input-weeks"
            type="text"
            inputMode="numeric"
            placeholder="wks"
            value={draft.total_weeks || ''}
            disabled={disabled}
            onChange={(e) => setTotalWeeks(e.target.value)}
          />
        </div>
      </div>
      <p className="tma-doc-field-hint">
        Only the CEO sees prices. Saving re-renders the document — and unlocks
        the proposal approval.
      </p>
    </div>
  );
}

/** Build an editable pricing draft aligned to the spec's fee blocks — one
    entry per setup / monthly block (defends against a null / drifted pricing).
    Mirror of the web panel's normalisePricing. */
function normalisePricing(
  pricing: ProposalPricing | null | undefined,
  spec: ProposalSpec | null | undefined,
): ProposalPricing {
  const setupFees = spec?.setup_fees || [];
  const monthlyFees = spec?.monthly_fees || [];
  const src = pricing || { setup: [], monthly: [], case_weeks: [], total_weeks: null };
  const setup = setupFees.map((_, i) => {
    const found = (src.setup || []).find((s) => s.i === i);
    return { i, price: found?.price ?? null };
  });
  const monthly = monthlyFees.map((_, i) => {
    const found = (src.monthly || []).find((s) => s.i === i);
    return { i, price: found?.price ?? null, overage: found?.overage ?? null };
  });
  const case_weeks = (src.case_weeks || []).map((cw) => ({
    case: cw.case || '',
    weeks: cw.weeks || '',
  }));
  return { setup, monthly, case_weeks, total_weeks: src.total_weeks ?? null };
}

export default ProposalTab;
